import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyFeeSettings } from '@/lib/meApi';
import { useNavigate } from 'react-router-dom';
import { X, Clock, Trash, Briefcase, CurrencyGbp, CheckCircle } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTimerStore, formatElapsed, msToHours } from '@/lib/timerStore';
import { useMattersStore } from '@/lib/mattersStore';
import { useTimeEntriesStore } from '@/lib/timeEntriesStore';
import { useAuthStore } from '@/features/auth/store';
import { fetchFeeRecords } from '@/lib/feesApi';
import { toast } from 'sonner';
import { ScrollLock } from '@/components/shared/ScrollLock';

const CATEGORY_TO_FEE_TYPE: Record<string, string> = {
  Conference: 'conference', Reading: 'hourly', Drafting: 'hourly',
  Hearing: 'hourly', Travel: 'hourly', Opinion: 'opinion',
  'Phone call': 'conference', Other: 'hourly',
};

interface Props {
  open: boolean;
  /** Initial elapsed ms (so the modal shows the running timer's value at open time) */
  initialMs: number;
  onClose: () => void;
  onSaved?: () => void;
}


const CATEGORIES = ['Conference', 'Reading', 'Drafting', 'Hearing', 'Travel', 'Opinion', 'Phone call', 'Other'];

export function TimeEntryModal({ open, initialMs, onClose, onSaved }: Props) {
  const navigate = useNavigate();
  const draftDescription = useTimerStore((s) => s.description);
  const draftMatterId = useTimerStore((s) => s.matterId);
  const reset = useTimerStore((s) => s.reset);
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const addEntry = useTimeEntriesStore((s) => s.addEntry);
  const user = useAuthStore((s) => s.user);
  useEffect(() => { if (open) fetchMatters(); }, [open, fetchMatters]);
  const matterOptions = matters.map((m) => ({ id: m.id, label: `${m.number} · ${m.name}` }));

  const [matterId, setMatterId] = useState(draftMatterId);
  const [description, setDescription] = useState(draftDescription);
  const [category, setCategory] = useState('Drafting');
  const [hoursText, setHoursText] = useState(msToHours(initialMs).toFixed(2));
  // The rate comes from the barrister's own rate card (Settings → Fees & VAT), not a guess.
  // It used to be a hardcoded £250 for everyone, whatever the barrister had set.
  const [rateText, setRateText] = useState('');
  const [nonBillable, setNonBillable] = useState(false);
  const [saving, setSaving] = useState(false);
  const rateSeeded = useRef(false);
  const feeSettingsQ = useQuery({
    queryKey: ['me-fee-settings'],
    queryFn: fetchMyFeeSettings,
    enabled: open && !!user?.barristerId,
    staleTime: 60_000,
  });

  // Reset local form when modal opens with a new initial value
  useEffect(() => {
    if (open) {
      setHoursText(msToHours(initialMs).toFixed(2));
      setMatterId(draftMatterId);
      setDescription(draftDescription);
      rateSeeded.current = false;
      setRateText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMs]);

  // Seed the rate once per opening from the rate card (the user can still overwrite it).
  useEffect(() => {
    if (!open || rateSeeded.current) return;
    const hourly = feeSettingsQ.data?.hourlyRate;
    if (hourly != null && hourly > 0) {
      setRateText(String(hourly));
      rateSeeded.current = true;
    }
  }, [open, feeSettingsQ.data]);

  if (!open) return null;

  const hours = parseFloat(hoursText) || 0;
  const rate = parseFloat(rateText) || 0;
  const total = hours * rate;
  // A billable entry needs a rate — without one it would silently save at £0.
  const valid = hours > 0 && (nonBillable || rate > 0);

  const submit = async () => {
    if (!matterId) { toast.error('Select a matter before saving'); return; }
    // Attribute the entry to a barrister. A barrister logs their own time (their profile);
    // a clerk/admin has no barrister profile, so they log time on behalf of the matter's
    // lead barrister. Without this a clerk hit "No barrister profile found — cannot save".
    const selectedMatter = matters.find((m) => m.id === matterId);
    const barristerId = user?.barristerId ?? selectedMatter?.leadBarristerId ?? null;
    if (!barristerId) { toast.error('This matter has no lead barrister to bill against'); return; }
    setSaving(true);
    try {
      await addEntry({
        caseId: matterId,
        barristerId,
        feeType: (CATEGORY_TO_FEE_TYPE[category] ?? 'hourly') as any,
        description: description || category,
        feeDate: new Date().toISOString().slice(0, 10),
        amount: Math.round(hours * rate * 100) / 100,
        durationMinutes: Math.round(hours * 60),
        rate,
        isBillable: !nonBillable,
      });

      reset();
      onClose();
      onSaved?.();

      // A3: After saving a billable entry, check for unbilled records and
      // surface a fee note creation prompt if there are any.
      if (!nonBillable) {
        try {
          const unbilled = await fetchFeeRecords({ caseId: matterId, unbilled: 'true' });
          const savedMatter = matters.find((m) => m.id === matterId);
          const matterLabel = savedMatter ? `${savedMatter.number} · ${savedMatter.name}` : 'this matter';
          if (unbilled.length >= 1) {
            const basePath = user?.memberType === 'admin' ? '/admin' : user?.memberType?.includes('clerk') ? '/clerk' : '/barrister';
            toast.success(`Time entry saved — ${unbilled.length} unbilled record${unbilled.length !== 1 ? 's' : ''} on ${matterLabel}`, {
              description: 'Ready to raise a fee note?',
              action: {
                label: 'Open Fees',
                onClick: () => navigate(`${basePath}/matters/${matterId}?tab=fees`),
              },
              duration: 8000,
            });
          } else {
            toast.success('Time entry saved');
          }
        } catch {
          toast.success('Time entry saved');
        }
      } else {
        toast.success('Time entry saved (non-billable)');
      }
    } catch {
      toast.error('Could not save time entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg overflow-hidden shadow-pop">
        <div className="flex items-center justify-between border-b border-border bg-brand-50 dark:bg-brand-900/30 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card">
              <Clock className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Save time entry</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Timer: {formatElapsed(initialMs)} · {msToHours(initialMs).toFixed(2)}h
              </div>
            </div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Matter
            </label>
            <select
              value={matterId}
              onChange={(e) => setMatterId(e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
            >
              <option value="">— Select a matter —</option>
              {matterOptions.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Activity category
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                    category === c
                      ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'border-border text-muted-foreground hover:border-brand-200 hover:text-foreground'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoFocus
              className="h-20 w-full resize-none rounded-md border border-border bg-card p-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
              placeholder="What did you do? e.g. Drafted skeleton argument, R v Whitfield"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Hours
              </label>
              <div className="relative">
                <Clock className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={hoursText}
                  onChange={(e) => setHoursText(e.target.value)}
                  className="tabular h-10 w-full rounded-md border border-border bg-card pl-9 pr-3 text-sm font-medium focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Rate / hr
              </label>
              <div className="relative">
                <CurrencyGbp className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={rateText}
                  onChange={(e) => { rateSeeded.current = true; setRateText(e.target.value); }}
                  className="tabular h-10 w-full rounded-md border border-border bg-card pl-9 pr-3 text-sm font-medium focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-900/40"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Total
              </label>
              <div className="tabular flex h-10 items-center rounded-md border border-border bg-muted/30 px-3 text-sm font-semibold text-foreground">
                £{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={nonBillable}
              onChange={(e) => setNonBillable(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
            />
            <span className="text-foreground">Non-billable</span>
            <span className="text-xs text-muted-foreground">— pro bono, internal, training</span>
          </label>

          <div className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10 p-3 text-xs text-blue-900 dark:text-blue-200">
            <Briefcase className="h-3.5 w-3.5 shrink-0" />
            Saved entries appear in <strong>Time &amp; Expenses</strong> and feed straight into the matter{'\u2019'}s <strong>Fees</strong> tab.
          </div>
        </CardContent>

        <div className="flex flex-col-reverse gap-3 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:px-6">
          <button
            onClick={() => { reset(); onClose(); }}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-danger hover:underline sm:justify-start"
          >
            <Trash className="h-3.5 w-3.5" />
            Discard timer
          </button>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button variant="outline" size="md" onClick={onClose}>Save & continue timer</Button>
            <Button variant="primary" size="md" leftIcon={<CheckCircle className="h-4 w-4" />} onClick={submit} disabled={!valid || saving}>
              {saving ? 'Saving…' : 'Save entry'}
            </Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
